import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  TableCell,
  TableRow,
  Alert,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Edit,
  FileText,
  Clock,
  MoreVertical,
  CheckCircle,
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

import { Button } from "../../../components/Button";
import { CustomSelector } from "../../../components/CustomSelector";
import { Badge } from "../../../components/Badge";
import { DataTable } from "../../../components/DataTable";
import { useTheme as useCustomTheme } from "../../../theme/useTheme";
import { colors } from "../../../theme/theme";
import { PLATFORM_META } from "../workflow/platformMeta";
import { getData } from "../../../components/hooks/getData";
import { useDelayedLoading } from "../../../components/hooks/useDelayedLoading";
import { useAuth } from "../../../components/contexts/AuthContext";
import apiClient from "../../../api-client";
import SuccessPopup from "../../../components/SuccessPopup";
import {
  DropdownMenuContent,
  DropdownMenuItem,
} from "../../../components/DropdownMenu";
import { getGroupDisplayStatus, getPieceDisplayStatus } from "../workflow/workflowUtils";
import { ContentCalendar } from "../ContentCalendar";
import { UpcomingPublications } from "../UpcomingPublications";
import { Card, CardContent } from "../../../components/Card";
import { ContentConfirmationModal } from "../Modals/ContentConfirmationModal";
import { UnreadThreadBadge } from "../../../components/UnreadThreadBadge";


const VIEW_OPTIONS = [
  { value: "week", label: "Semanal" },
  { value: "month", label: "Mensual" },
];
const DEFAULT_PAGE_SIZE = 25;

// Cabeceras de la tabla (las filas las pintan renderPieceRow y la fila de grupo).
const CLIENT_CONTENT_COLUMNS = [
  { key: "content", header: "Contenido", minWidth: { xs: 280, md: 520 } },
  { key: "platform", header: "Plataforma" },
  { key: "status", header: "Estado" },
  {
    key: "created",
    header: "Fecha de creación",
    headerSx: { paddingLeft: { xs: "4px", md: "8px" } },
  },
  {
    key: "actions",
    header: "Acciones",
    align: "center",
    width: 44,
    headerSx: { padding: { xs: "6px 4px", md: "10px 6px" } },
  },
];
const isGroupEntry = (item) => item?.type === "group";

const getStartOfWeek = (date) => {
  const dayIndex = (date.getDay() + 6) % 7;
  const start = new Date(date);
  start.setDate(date.getDate() - dayIndex);
  start.setHours(0, 0, 0, 0);
  return start;
};

const formatMonthYear = (date) => {
  const month = date.toLocaleDateString("es-ES", { month: "long" });
  const monthLabel = `${month.charAt(0).toUpperCase()}${month.slice(1)}`;
  return `${monthLabel} de ${date.getFullYear()}`;
};

export function ClientContent() {
  const theme = useTheme();
  const { neutrals } = useCustomTheme();
  const {
    setPageContext,
    contentGeneratorRefreshKey,
    selectedFilter,
    clientIds,
    filterRefresh,
  } = useOutletContext();
  const { interceptorReady } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const openPiece = useCallback((pieceId) => navigate(`/contenido/${pieceId}`), [navigate]);
  const openGroup = useCallback((groupId) => navigate(`/contenido/grupo/${groupId}`), [navigate]);

  // For backward compatibility, keep clientId for single client scenarios (used in ContentCalendar)
  const clientId = clientIds?.length === 1 ? clientIds[0] : null;

  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalCount, setTotalCount] = useState(0);
  const [piecesRefresh, setPiecesRefresh] = useState(0);
  const [statusStats, setStatusStats] = useState({
    client_review: 0,
    client_approved: 0,
    scheduled: 0,
    published: 0,
  });
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedContentForMenu, setSelectedContentForMenu] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [isPopupError, setIsPopupError] = useState(false);
  const [localContentUpdates, setLocalContentUpdates] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});
  const [viewMode, setViewMode] = useState("table"); // "table" or "calendar"
  const [calendarRefresh, setCalendarRefresh] = useState(0);
  const [calendarCurrentDate, setCalendarCurrentDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState("week");
  const pendingThreadToOpenRef = useRef(null);
  const pendingThreadConsumedRef = useRef(true);

  const weekStart = useMemo(() => getStartOfWeek(calendarCurrentDate), [calendarCurrentDate]);

  useEffect(() => {
    setPageContext({
      title: "Contenido",
      showTitle: true,
    });
  }, [setPageContext]);

  useEffect(() => {
    const threadParam = searchParams.get("thread");
    const pieceParam = searchParams.get("piece");
    const groupParam = searchParams.get("group");
    if (!threadParam && !pieceParam && !groupParam) return;

    // Notification links: a group or a piece open their pages.
    if (groupParam) {
      navigate(`/contenido/grupo/${groupParam}`, { replace: true });
      return;
    }
    if (pieceParam) {
      navigate(`/contenido/${pieceParam}`, { replace: true });
      return;
    }
    pendingThreadToOpenRef.current = threadParam;
    pendingThreadConsumedRef.current = false;
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams, navigate]);

  // Build content pieces endpoint with useMemo (like Leads.jsx and Incidents.jsx)
  const contentPiecesEndpoint = React.useMemo(() => {
    if (!clientIds || clientIds.length === 0) return null;

    const queryParts = [];

    // Only add client filter when a specific filter is selected
    // When no filter (global), the backend automatically filters by user's accessible clients
    if (selectedFilter && selectedFilter !== "global") {
      // Specific client selected in filter
      queryParts.push(`client=${selectedFilter}`);
    }
    // For single client users or global view, let the backend handle filtering automatically

    if (searchTerm) {
      queryParts.push(`search=${encodeURIComponent(searchTerm)}`);
    }

    if (selectedStatus && selectedStatus !== "all") {
      if (selectedStatus === "review") {
        queryParts.push(`status=client_review`);
      } else if (selectedStatus === "accepted") {
        // Aceptado = aprobado y aún sin programar ni publicar.
        queryParts.push(`status=client_approved`);
        queryParts.push(`publication_status=none`);
      } else if (selectedStatus === "scheduled" || selectedStatus === "published") {
        queryParts.push(`publication_status=${selectedStatus}`);
      } else {
        queryParts.push(`status=${selectedStatus}`);
      }
    }

    // Add pagination parameters
    queryParts.push(`page=${page + 1}`); // Backend uses 1-based indexing
    queryParts.push(`page_size=${pageSize}`);

    const endpoint = `content-generation/pieces/?${queryParts.join("&")}`;
    console.log("🔥 ClientContent endpoint:", endpoint, "selectedFilter:", selectedFilter, "clientIds:", clientIds);
    return endpoint;
  }, [clientIds, selectedFilter, searchTerm, selectedStatus, page, pageSize]);

  const { data: contentPiecesResponse, isLoading: isLoadingContentPieces } =
    getData(
      contentPiecesEndpoint,
      interceptorReady && Boolean(contentPiecesEndpoint),
      [contentPiecesEndpoint, contentGeneratorRefreshKey, piecesRefresh, filterRefresh]
    );

  // Debug: ver si los datos cambian
  console.log("🔵 contentPiecesResponse:", contentPiecesResponse?.results?.length, "pieces, isLoading:", isLoadingContentPieces);


  // Calendar adaptations endpoint with useMemo (like Leads.jsx pattern)
  const shouldFetchCalendarAdaptations = viewMode === "calendar";

  const calendarAdaptationsEndpoint = React.useMemo(() => {
    if (!shouldFetchCalendarAdaptations) return null;
    if (!clientIds || clientIds.length === 0) return null;

    const queryParts = [];

    // Only add client filter when a specific filter is selected
    if (selectedFilter && selectedFilter !== "global") {
      queryParts.push(`client=${selectedFilter}`);
    }
    // For single client users or global view, let the backend handle filtering automatically

    queryParts.push(`scheduled=true`);

    return `content-generation/adaptations/?${queryParts.join("&")}`;
  }, [shouldFetchCalendarAdaptations, clientIds, selectedFilter]);

  const { data: calendarAdaptationsResponse } = getData(
    calendarAdaptationsEndpoint,
    interceptorReady && Boolean(calendarAdaptationsEndpoint),
    [calendarAdaptationsEndpoint, contentGeneratorRefreshKey, calendarRefresh, filterRefresh]
  );

  const calendarAdaptations = useMemo(() => {
    if (!calendarAdaptationsResponse) return [];
    const adaptations = Array.isArray(calendarAdaptationsResponse?.results)
      ? calendarAdaptationsResponse.results
      : Array.isArray(calendarAdaptationsResponse)
        ? calendarAdaptationsResponse
        : [];
    return adaptations;
  }, [calendarAdaptationsResponse]);

  const showTableSkeleton = useDelayedLoading(isLoadingContentPieces, 150);

  // Upcoming publications: next to the table on large screens, above it otherwise.
  const isLgScreen = useMediaQuery(theme.breakpoints.up("lg"));
  const upcomingLayout = isLgScreen ? "side" : "top";
  const goToCalendar = () => {
    setCalendarRefresh((prev) => prev + 1);
    setViewMode("calendar");
  };

  // Update total count and stats from paginated response
  useEffect(() => {
    if (contentPiecesResponse?.count !== undefined) {
      setTotalCount(contentPiecesResponse.count);
    }
    if (contentPiecesResponse?.status_stats) {
      setStatusStats({
        client_review: 0,
        client_approved: 0,
        scheduled: 0,
        published: 0,
        ...contentPiecesResponse.status_stats,
      });
    }
  }, [contentPiecesResponse]);

  // Reset to page 0 when filters change
  useEffect(() => {
    setPage(0);
  }, [selectedStatus, searchTerm, selectedFilter]);

  const contentPieces = useMemo(() => {
    if (!contentPiecesResponse) return [];
    const pieces = Array.isArray(contentPiecesResponse?.results)
      ? contentPiecesResponse.results
      : Array.isArray(contentPiecesResponse)
        ? contentPiecesResponse
        : [];

    // Apply local updates to flat pieces and grouped pieces
    return pieces.map((piece) => {
      if (isGroupEntry(piece)) {
        return {
          ...piece,
          pieces: (piece.pieces || []).map((groupPiece) => {
            const localUpdate = localContentUpdates[groupPiece.id];
            return localUpdate ? { ...groupPiece, ...localUpdate } : { ...groupPiece, unopened_unread_notifications: groupPiece.unopened_unread_notifications || 0 };
          }),
        };
      }
      const localUpdate = localContentUpdates[piece.id];
      return localUpdate ? { ...piece, ...localUpdate } : { ...piece, unopened_unread_notifications: piece.unopened_unread_notifications || 0 };
    });
  }, [contentPiecesResponse, localContentUpdates]);

  const flatContentPieces = useMemo(
    () =>
      contentPieces.flatMap((piece) =>
        isGroupEntry(piece) ? piece.pieces || [] : [piece]
      ),
    [contentPieces]
  );

  useEffect(() => {
    setExpandedGroups((prev) => {
      const availableGroupIds = new Set(
        contentPieces
          .filter((piece) => isGroupEntry(piece) && piece.id != null)
          .map((piece) => String(piece.id))
      );

      let changed = false;
      const next = { ...prev };

      availableGroupIds.forEach((groupId) => {
        if (next[groupId] === undefined) {
          next[groupId] = false;
          changed = true;
        }
      });

      Object.keys(next).forEach((groupId) => {
        if (!availableGroupIds.has(groupId)) {
          delete next[groupId];
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [contentPieces]);


  // Búsqueda en vivo (debounce): el buscador del toolbar de DataTable no tiene botón de buscar.
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === searchTerm) return undefined;
    const timer = setTimeout(() => {
      setSearchTerm(trimmed);
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput, searchTerm]);

  const handleStatusChange = useCallback((status) => {
    setSelectedStatus(status);
    setPage(0);
  }, []);

  const totalPages = totalCount > -1 ? Math.ceil(totalCount / pageSize) : null;
  const canGoBack = page > 0 && !isLoadingContentPieces;
  const canGoNext =
    !isLoadingContentPieces &&
    (totalPages !== null
      ? page < totalPages - 1
      : Boolean(contentPiecesResponse?.next));

  const handleChangePage = (newPage) => {
    if (isLoadingContentPieces || newPage === page) return;
    if (newPage > page && !canGoNext) return;
    if (newPage < page && !canGoBack) return;
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (nextPageSize) => {
    setPageSize(nextPageSize);
    setPage(0);
  };

  const handleMenuOpen = (event, content) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedContentForMenu(content);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedContentForMenu(null);
  };


  const handleSuggestChanges = () => {
    if (!selectedContentForMenu) return;
    handleMenuClose();
    if (selectedContentForMenu.type === "group") {
      openGroup(selectedContentForMenu.id);
    } else if (selectedContentForMenu.id) {
      openPiece(selectedContentForMenu.id);
    }
  };

  const handleAcceptContent = () => {
    setShowApproveModal(true);
  };

  const handleConfirmApprove = async () => {
    if (!selectedContentForMenu?.id) return;
    setIsApproving(true);
    try {
      if (selectedContentForMenu.type === "group") {
        await apiClient.post(
          `/content-generation/groups/${selectedContentForMenu.id}/approve/`
        );
        setPiecesRefresh((prev) => prev + 1);
      } else {
        await apiClient.post(
          `/content-generation/pieces/${selectedContentForMenu.id}/approve/`
        );

        // Update local state instead of full refresh
        setLocalContentUpdates(prev => ({
          ...prev,
          [selectedContentForMenu.id]: {
            status: 'client_approved'
          }
        }));
      }

      setPopupMessage("Contenido aprobado correctamente");
      setIsPopupError(false);
      setShowSuccessPopup(true);
      setShowApproveModal(false);
    } catch (error) {
      console.error("Error approving content:", error);
      setPopupMessage("Error al aprobar el contenido");
      setIsPopupError(true);
      setShowSuccessPopup(true);
    } finally {
      setIsApproving(false);
      handleMenuClose();
    }
  };

  const handleRowClick = (content, groupId = null) => {
    // A grouped piece opens its group's joint view, on the summary tab.
    const pieceGroupId = groupId ?? content?.group_id;
    if (pieceGroupId) openGroup(pieceGroupId);
    else if (content?.id) openPiece(content.id);
  };

  const menuOpen = Boolean(menuAnchorEl);
  const normalizedMenuStatus = selectedContentForMenu?.status
    ? selectedContentForMenu.status === "client_review"
      ? "review"
      : selectedContentForMenu.status === "client_approved"
        ? "accepted"
        : selectedContentForMenu.status
    : null;
  const canReviewActions = normalizedMenuStatus === "review";

  useEffect(() => {
    const threadId = pendingThreadToOpenRef.current;
    if (!threadId || pendingThreadConsumedRef.current || !interceptorReady) return;

    const matchingPiece = flatContentPieces.find(
      (piece) => String(piece.thread_id) === String(threadId)
    );
    if (!matchingPiece?.id) return;

    pendingThreadConsumedRef.current = true;
    pendingThreadToOpenRef.current = null;
    navigate(`/contenido/${matchingPiece.id}`, { replace: true });
  }, [interceptorReady, flatContentPieces, navigate]);

  const toggleGroupExpansion = useCallback((groupId) => {
    if (groupId == null) return;
    setExpandedGroups((prev) => {
      const key = String(groupId);
      return {
        ...prev,
        [key]: !prev[key],
      };
    });
  }, []);

  const renderPlatformIcons = (platforms) => (
    <Box
      sx={{
        display: "flex",
        gap: 0.5,
        alignItems: "center",
      }}
    >
      {(platforms || []).map((platform) => {
        const meta = PLATFORM_META[platform];
        if (!meta) return null;
        const { Icon, color, label } = meta;
        return (
          <Tooltip key={platform} title={label} arrow>
            <Icon sx={{ fontSize: 16, color }} />
          </Tooltip>
        );
      })}
      {(!platforms || platforms.length === 0) && (
        <Typography variant="caption" color="text.secondary">
          -
        </Typography>
      )}
    </Box>
  );

  const renderPieceRow = (content, rowOptions = {}) => {
    const { nested = false, keyPrefix = "piece", groupId = null } = rowOptions;
    const statusInfo = getPieceDisplayStatus(content);

    const handlePieceClick = () => handleRowClick(content, groupId);

    return (
      <TableRow
        key={`${keyPrefix}-${content.id}`}
        onClick={handlePieceClick}
        sx={{
          cursor: "pointer",
          backgroundColor: nested ? neutrals.gray[50] : "transparent",
          "& > td:first-of-type": {
            borderLeft: nested ? `3px solid ${colors.primary.extralight}` : "3px solid transparent",
          },
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
          },
          transition: "background-color 0.2s ease",
        }}
      >
        <TableCell sx={{ minWidth: { xs: 280, md: 520 } }}>
          <Box sx={{ minWidth: 0, display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box sx={{ minWidth: 0, maxWidth: { xs: 220, md: 460 } }}>
              <Tooltip title={content.title} arrow>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    pl: nested ? 2 : 0,
                    fontSize: {
                      xs: "0.75rem",
                      md: "0.875rem",
                    },
                  }}
                >
                  {content.title}
                </Typography>
              </Tooltip>
            </Box>
            <UnreadThreadBadge count={content.unopened_unread_notifications} />
          </Box>
        </TableCell>
        <TableCell>{renderPlatformIcons(content.generated_platforms)}</TableCell>
        <TableCell>
          <Badge variant="outline" color={statusInfo.color}>
            {statusInfo.label}
          </Badge>
        </TableCell>
        <TableCell>
          <Tooltip
            title={
              content.created_at
                ? new Date(content.created_at).toLocaleDateString(
                  "es-ES",
                  {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  }
                )
                : "Sin fecha"
            }
            arrow
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                minWidth: 0,
              }}
            >
              <Clock
                size={12}
                color={theme.palette.text.secondary}
              />
              <Typography
                variant="caption"
                color="text.primary"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: { xs: "0.7rem", md: "0.75rem" },
                }}
              >
                {content.created_at
                  ? new Date(content.created_at).toLocaleDateString(
                    "es-ES",
                    {
                      day: "numeric",
                      month: "short",
                      hour: "2-digit",
                      minute: "2-digit",
                    }
                  )
                  : "-"}
              </Typography>
            </Box>
          </Tooltip>
        </TableCell>
        <TableCell
          align="center"
          sx={{ padding: { xs: "6px 4px", md: "10px 6px" } }}
        >
          {content.status === "client_review" && !nested && (
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); handleMenuOpen(e, content); }}
              sx={{
                color: theme.palette.text.secondary,
                padding: "4px",
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <MoreVertical size={16} />
            </IconButton>
          )}
        </TableCell>
      </TableRow>
    );
  };

  if (!clientIds || clientIds.length === 0) {
    return (
      <Alert severity="error">
        No se encontró el cliente asociado a tu cuenta. Contacta con soporte.
      </Alert>
    );
  }

  const upcomingPanel = (
    <UpcomingPublications
      layout={upcomingLayout}
      filters={selectedFilter && selectedFilter !== "global" ? { client: selectedFilter } : {}}
      refreshKeys={[piecesRefresh, contentGeneratorRefreshKey, filterRefresh]}
      showClientName={clientIds.length > 1 && (!selectedFilter || selectedFilter === "global")}
      onGoToCalendar={goToCalendar}
      onItemClick={(item) => (item.groupId ? openGroup(item.groupId) : openPiece(item.pieceId))}
    />
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "calc(100vh - 165px)",
        // Panel above the table: grow the page (it scrolls) instead of squeezing the table.
        minHeight: upcomingLayout === "top" && viewMode === "table" ? 960 : 0,
        overflow: "hidden",
        flex: 1,
      }}
    >
      {viewMode === "table" ? (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: upcomingLayout === "side" ? "row" : "column",
            gap: 2,
          }}
        >
        {upcomingLayout === "top" && upcomingPanel}
        <DataTable
          columns={CLIENT_CONTENT_COLUMNS}
          rows={contentPieces}
          getRowId={(content) =>
            isGroupEntry(content) ? `group-${content.id}` : `piece-${content.id}`
          }
          renderRow={(content) => {
            if (!isGroupEntry(content)) {
              return renderPieceRow(content);
            }

            const groupId = String(content.id);
            const isExpanded = Boolean(expandedGroups[groupId]);
            const groupStatusInfo = getGroupDisplayStatus(content);
            const groupPieces = content.pieces || [];

            return (
              <React.Fragment key={`group-${groupId}`}>
                <TableRow
                  onClick={() => toggleGroupExpansion(content.id)}
                  sx={{
                    cursor: "pointer",
                    backgroundColor: "transparent",
                    "& > td:first-of-type": {
                      borderLeft: isExpanded ? `3px solid ${colors.primary.main}` : "3px solid transparent",
                    },
                    "&:hover": {
                      backgroundColor: theme.palette.action.hover,
                    },
                    transition: "background-color 0.2s ease",
                  }}
                >
                  <TableCell>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        maxWidth: { xs: 160, md: 300 },
                      }}
                    >
                      <ChevronDown
                        size={16}
                        style={{
                          transform: isExpanded
                            ? "rotate(0deg)"
                            : "rotate(-90deg)",
                          transition: "transform 0.2s ease",
                        }}
                      />
                      <Tooltip
                        title={`${content.name || "Grupo"} (${groupPieces.length} contenidos)`}
                        arrow
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontSize: {
                              xs: "0.75rem",
                              md: "0.875rem",
                            },
                          }}
                        >
                          {content.name || "Grupo"}{" "}
                          <Badge variant="outline" size="sm" sx={{ ml: 1 }}>
                            {content.piece_count ?? groupPieces.length} Contenidos
                          </Badge>
                        </Typography>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {renderPlatformIcons(content.generated_platforms)}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" color={groupStatusInfo.color}>
                      {groupStatusInfo.label}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Tooltip
                      title={
                        content.updated_at
                          ? new Date(content.updated_at).toLocaleDateString(
                            "es-ES",
                            {
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            }
                          )
                          : "Sin fecha"
                      }
                      arrow
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          minWidth: 0,
                        }}
                      >
                        <Clock
                          size={12}
                          color={theme.palette.text.secondary}
                        />
                        <Typography
                          variant="caption"
                          color="text.primary"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontSize: { xs: "0.7rem", md: "0.75rem" },
                          }}
                        >
                          {content.updated_at
                            ? new Date(content.updated_at).toLocaleDateString(
                              "es-ES",
                              {
                                day: "numeric",
                                month: "short",
                                hour: "2-digit",
                                minute: "2-digit",
                              }
                            )
                            : "-"}
                        </Typography>
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ padding: { xs: "6px 4px", md: "10px 6px" } }}
                  >
                    {content.status === "client_review" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          openGroup(content.id);
                        }}
                        sx={{ mr: 0.5, display: { xs: "none", md: "inline-flex" } }}
                      >
                        Revisar tanda
                      </Button>
                    )}
                    {content.status === "client_review" && (
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); handleMenuOpen(e, content); }}
                        sx={{
                          color: theme.palette.text.secondary,
                          padding: "4px",
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                      >
                        <MoreVertical size={16} />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
                {isExpanded &&
                  groupPieces.map((piece) =>
                    renderPieceRow(piece, {
                      nested: true,
                      keyPrefix: `group-${groupId}-piece`,
                      groupId,
                    })
                  )}
              </React.Fragment>
            );
          }}
          loading={showTableSkeleton}
          skeletonRows={Math.min(pageSize, 8)}
          emptyContent={
            <Box
              sx={{
                p: 4,
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <FileText size={48} color={neutrals.gray[400]} style={{ marginBottom: 12 }} />
              <Typography sx={{ color: neutrals.gray[600], fontSize: "0.95rem" }}>
                No se encontraron contenidos con los filtros actuales.
              </Typography>
            </Box>
          }
          stickyHeader
          tableMinWidth={350}
          sx={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            // La tabla ocupa el alto disponible y hace scroll (vertical y lateral) dentro de la card.
            "& .MuiTableContainer-root": {
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
            },
          }}
          searchValue={searchInput}
          onSearchChange={setSearchInput}
          searchPlaceholder="Buscar contenidos..."
          tabs={[
            {
              value: "all",
              label: "Todos",
              count:
                statusStats.client_review +
                statusStats.client_approved +
                statusStats.scheduled +
                statusStats.published,
            },
            { value: "review", label: "Revisión", count: statusStats.client_review },
            { value: "accepted", label: "Aceptados", count: statusStats.client_approved },
            { value: "scheduled", label: "Programados", count: statusStats.scheduled },
            { value: "published", label: "Publicados", count: statusStats.published },
          ]}
          activeTab={selectedStatus}
          onTabChange={handleStatusChange}
          toolbarEnd={
            <Button
              size="sm"
              onClick={goToCalendar}
            >
              <Calendar size={16} />
              Calendario
            </Button>
          }
          page={page}
          rowsPerPage={pageSize}
          totalCount={totalCount}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          hidePaginationWhenEmpty={false}
        />
        {upcomingLayout === "side" && upcomingPanel}
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            borderRadius: "12px",
            border: `1px solid ${theme.palette.divider}`,
            overflow: "hidden",
            padding: 0,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
              p: 2,
              minHeight: 72,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCalendarCurrentDate(new Date())}
                  sx={{
                    paddingInline: "0px",
                    borderColor: neutrals.gray[300],
                    fontWeight: 600,
                  }}
                >
                  Hoy
                </Button>

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                  <IconButton
                    onClick={() => {
                      if (calendarView === "month") {
                        setCalendarCurrentDate(
                          new Date(
                            calendarCurrentDate.getFullYear(),
                            calendarCurrentDate.getMonth() - 1,
                            1
                          )
                        );
                      } else {
                        const previousWeek = new Date(calendarCurrentDate);
                        previousWeek.setDate(calendarCurrentDate.getDate() - 7);
                        setCalendarCurrentDate(previousWeek);
                      }
                    }}
                    size="small"
                    sx={{
                      width: 32,
                      height: 32,
                      color: neutrals.gray[700],
                      "&:hover": {
                        backgroundColor: neutrals.gray[100],
                      },
                    }}
                  >
                    <ChevronLeft size={18} />
                  </IconButton>
                  <IconButton
                    onClick={() => {
                      if (calendarView === "month") {
                        setCalendarCurrentDate(
                          new Date(
                            calendarCurrentDate.getFullYear(),
                            calendarCurrentDate.getMonth() + 1,
                            1
                          )
                        );
                      } else {
                        const nextWeek = new Date(calendarCurrentDate);
                        nextWeek.setDate(calendarCurrentDate.getDate() + 7);
                        setCalendarCurrentDate(nextWeek);
                      }
                    }}
                    size="small"
                    sx={{
                      width: 32,
                      height: 32,
                      color: neutrals.gray[700],
                      "&:hover": {
                        backgroundColor: neutrals.gray[100],
                      },
                    }}
                  >
                    <ChevronRight size={18} />
                  </IconButton>
                </Box>

                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: "1.2rem",
                    display: "inline-flex",
                    justifyContent: "flex-start",
                    flexShrink: 0,
                  }}
                >
                  {calendarView === "month"
                    ? formatMonthYear(calendarCurrentDate)
                    : formatMonthYear(weekStart)}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1, marginLeft: "auto" }}>
                <Box sx={{ minWidth: 160 }}>
                  <CustomSelector
                    value={calendarView}
                    onChange={(value) => setCalendarView(value)}
                    options={VIEW_OPTIONS}
                    placeholder="Vista"
                  />
                </Box>
                <Button
                  onClick={() => {
                    setPiecesRefresh((prev) => prev + 1);
                    setViewMode("table");
                  }}
                >
                  <ChevronLeft size={16} />
                  Volver atrás
                </Button>
              </Box>
            </>
          </Box>

          {/* Calendar view */}
          <Card
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              border: "none",
              boxShadow: "none",
            }}
          >
            <CardContent
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                padding: "16px",
              }}
            >
              <ContentCalendar
                clientId={clientId}
                platformAdaptations={calendarAdaptations}
                onContentClick={handleRowClick}
                showClientName={false}
                currentDate={calendarCurrentDate}
                onCurrentDateChange={setCalendarCurrentDate}
                calendarView={calendarView}
                onCalendarViewChange={setCalendarView}
                showHeader={false}
              />
            </CardContent>
          </Card>
        </Box>
      )}

      <DropdownMenuContent
        anchorEl={menuAnchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {canReviewActions && (
          <DropdownMenuItem onClick={handleSuggestChanges}>
            <Edit size={16} />
            Sugerir cambios
          </DropdownMenuItem>
        )}
        {canReviewActions && (
          <DropdownMenuItem onClick={handleAcceptContent}>
            <CheckCircle size={16} />
            Aceptar
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>

      {/* Approve Content Confirmation Modal */}
      <ContentConfirmationModal
        open={showApproveModal}
        onClose={() => setShowApproveModal(false)}
        onConfirm={handleConfirmApprove}
        action="approve"
        contentData={{
          type: selectedContentForMenu?.type,
          name: selectedContentForMenu?.type === "group" ? selectedContentForMenu?.name : undefined,
          group_id: selectedContentForMenu?.group_id,
          group_name: selectedContentForMenu?.group_name,
          title: selectedContentForMenu?.title,
        }}
        isLoading={isApproving}
      />

      <SuccessPopup
        message={popupMessage}
        isOpen={showSuccessPopup}
        onClose={() => {
          setShowSuccessPopup(false);
          setIsPopupError(false);
        }}
        autoHideDuration={3000}
        error={isPopupError}
      />

    </Box>
  );
}
